/**
 * Google click-ID (gclid) first-party cookie.
 *
 * Google Ads attributes conversions to ad clicks using the `gclid` query
 * parameter that Ads auto-appends to destination URLs when auto-tagging is
 * enabled (default). We capture that value the first time we see it and
 * persist it to a first-party cookie so we can forward it to our backend
 * (Composer) at signup / upgrade time, which in turn includes it on the
 * GA4 Measurement Protocol event so Google Ads bidding gets full
 * click-level attribution even for desktop users whose conversions we
 * report server-side.
 *
 * Scope constraints mirror `reddit/cookie.ts`:
 * - NOT HttpOnly: middleware (server) and the signup client call both
 *   need to read this value. The cookie contains only a Google-issued
 *   tracking identifier; no authentication material.
 * - 90-day Max-Age: Google's default click-through attribution window
 *   for conversion tracking is 30 days but extends up to 90. We match
 *   the upper bound so late conversions still carry the click-ID.
 * - SameSite=Lax: required for the ad-landing case (top-level
 *   navigation from the Ads SERP). Strict would break first-party
 *   capture.
 */

export const GOOGLE_CLICK_ID_COOKIE_NAME = "nrs_gclid";

/**
 * 90 days. Google Ads default click-through conversion window tops out at
 * 90 days; we use the upper bound so delayed conversions retain
 * attribution. Explicitly NOT read from env / config: cookie lifetime is
 * a product decision locked in the code so analytics expectations stay
 * deterministic across deployments.
 */
export const GOOGLE_CLICK_ID_COOKIE_MAX_AGE_SEC = 90 * 24 * 60 * 60;

/**
 * Max accepted length. Current Google `gclid` values are ~90 chars; cap
 * generously but aggressively enough to prevent cookie-bomb abuse via a
 * crafted `?gclid=` query string.
 */
export const GOOGLE_CLICK_ID_MAX_LENGTH = 256;

/**
 * Query parameter Google Ads appends to ad landing URLs when auto-tagging
 * is enabled on the account. Separate `gbraid` (iOS app) and `wbraid`
 * (web-to-app) parameters also exist; we capture only `gclid` here
 * because our conversion path is browser-first. If we ever launch a
 * mobile app campaign, add a sibling module (e.g. gbraid/cookie.ts).
 */
export const GOOGLE_CLICK_ID_QUERY_PARAM = "gclid";

/**
 * Returns a sanitized gclid value or null if the input is invalid.
 *
 * Google gclids use URL-safe base64 (alphanumeric + `-` + `_`). We
 * accept the broader URL-safe charset shared with Reddit's validator
 * (adds `.` and `~`) to stay robust against format changes; anything
 * outside that charset is rejected as attacker-controlled garbage.
 */
export function sanitizeGoogleClickId(raw: unknown): string | null {
  if (typeof raw !== "string") return null;
  const trimmed = raw.trim();
  if (!trimmed) return null;
  if (trimmed.length > GOOGLE_CLICK_ID_MAX_LENGTH) return null;
  if (!/^[A-Za-z0-9._~-]+$/.test(trimmed)) return null;
  return trimmed;
}

/**
 * Reads the Google click-ID from a raw Cookie header string (used by
 * server code that only sees the raw header). Returns null if the
 * cookie is absent or the stored value fails sanitization.
 */
export function readGoogleClickIdFromCookieHeader(
  header: string | null | undefined
): string | null {
  if (!header) return null;
  const match = header.match(
    new RegExp(`(?:^|; )${GOOGLE_CLICK_ID_COOKIE_NAME}=([^;]*)`)
  );
  if (!match) return null;
  try {
    return sanitizeGoogleClickId(decodeURIComponent(match[1]));
  } catch {
    return null;
  }
}

/**
 * Client-only reader: stored cookie wins, falls back to the current
 * URL's `?gclid=` query parameter when present. The URL fallback
 * covers the very first landing before middleware has set the cookie
 * (e.g. in a test environment or on a cached 304 response).
 *
 * Returns null outside a browser context.
 */
export function getGoogleClickIdFromBrowser(): string | null {
  if (typeof document === "undefined") return null;
  const fromCookie = readGoogleClickIdFromCookieHeader(document.cookie);
  if (fromCookie) return fromCookie;
  if (typeof window !== "undefined") {
    try {
      const fromUrl = new URLSearchParams(window.location.search).get(
        GOOGLE_CLICK_ID_QUERY_PARAM
      );
      return sanitizeGoogleClickId(fromUrl);
    } catch {
      return null;
    }
  }
  return null;
}

/**
 * Serializes the Set-Cookie directive used by our middleware. Kept here
 * (not inlined in middleware.ts) so the cookie attributes are defined
 * once and the format is unit-testable.
 *
 * `isSecureContext` must be true in production (HTTPS) and false for
 * local HTTP development; Secure cookies are silently dropped by
 * browsers on plain HTTP so the flag has to be omitted when serving
 * over http://.
 */
export function buildGoogleClickIdSetCookie(
  value: string,
  options: { isSecureContext: boolean }
): string {
  const parts = [
    `${GOOGLE_CLICK_ID_COOKIE_NAME}=${encodeURIComponent(value)}`,
    `Max-Age=${GOOGLE_CLICK_ID_COOKIE_MAX_AGE_SEC}`,
    "Path=/",
    "SameSite=Lax",
  ];
  if (options.isSecureContext) {
    parts.push("Secure");
  }
  return parts.join("; ");
}
